import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Autocomplete,
  Box,
  DialogTitle,
  DialogActions,
  Paper,
  Grid,
  Avatar,
  IconButton,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PrintIcon from '@mui/icons-material/Print';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { CREATE_CONTAINER_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useGlobalCore } from '../../../../core/modules/global';
import { isEmpty } from 'lodash';
import { useCompanyCore } from '../../../../core/modules/company';
import { useContainerCore } from '../../../../core/modules/container';
import { useMasterCore } from '../../../../core/modules/master';
import { useLocationCore } from '../../../../core/modules/location';
import { useWarehouseCore } from '../../../../core/modules/warehouse';
import { PostContainerPayloadRequest } from '../../../../core/modules/container/interfaces/post-container';
import {
  DESTINY_CONTAINER_TYPE,
  DESTINY_CONTAINER_TYPES,
} from '../../../../core/constants/containerTypes';
import { ContainerCategory } from '../../../../core/interfaces/category.interface';
import { ERROR } from '../../../../core/constants/errorCode';
import { Container } from '../../../../core/interfaces/container.interface';
import { usePrintLabel } from '../../../../core/hooks/usePrintLabel';
import { useLocation } from 'react-router-dom';
import CategorySelect from '../../shared/CategorySelect';
import CopyButton from '../../shared/CopyButton';

// Interfaz para el modal de éxito
interface ContainerSuccessData {
  id: number;
  code: string;
  description: string;
  container_type_name: string;
  container_category_name: string;
  container_status_name: string;
  location_description: string;
}

const schema = yup.object().shape({
  container_status_id: yup.number().required('Campo obligatorio'),
  container_category_id: yup.number().required('Campo obligatorio'),
  container_recipient_type_id: yup.number().required('Campo obligatorio'),
  description: yup.string().optional(),
  channel_type_id: yup.number().optional(),
  price_range_id: yup.number().optional(),
  location_id: yup.number().required('Campo obligatorio'),
});

const CreateDialog = () => {
  const { companyId } = useCompanyCore();
  const { dialogs, closeDialog } = useGlobalCore();
  const location = useLocation();
  const { containerTypes, channelTypes, containerCategories, priceRanges, containerRecipientTypes, getTypes } =
    useMasterCore();
  const { locations, getLocations } = useLocationCore();
  const { warehouses, getWarehouses } = useWarehouseCore();
  const { options, errorCode, getContainers, postContainer, postContainerFile } =
    useContainerCore();
  const { printDestinationContainer } = usePrintLabel();

  const [loading, setLoading] = useState(false);
  const [containerType, setContainerType] = useState<number>();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdContainer, setCreatedContainer] = useState<ContainerSuccessData | null>(null);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  
  // Constante para máximo de imágenes (parametrizable)
  const MAX_CONTAINER_FILES = 10;

  const { control, formState, handleSubmit, resetField } = useForm({
    mode: 'onChange',
    defaultValues: {
      container_status_id: DESTINY_CONTAINER_TYPE.OPENED,
    },
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  // Cargar tipos de recipiente al abrir el diálogo
  useEffect(() => {
    if (companyId && Array.isArray(dialogs) && dialogs.includes(CREATE_CONTAINER_DIALOG)) {
      getTypes(companyId, 'containerRecipient');
    }
  }, [companyId, dialogs, getTypes]);

  // Cargar ubicaciones al abrir el diálogo
  useEffect(() => {
    if (companyId && Array.isArray(dialogs) && dialogs.includes(CREATE_CONTAINER_DIALOG)) {
      getLocations(companyId);
    }
  }, [companyId, dialogs, getLocations]);

  useEffect(() => {
    if (companyId) {
      getWarehouses(companyId);
    }
  }, [companyId, getWarehouses]);

  const locationOptions = useMemo(() => {
    return locations.map(loc => {
      const warehouse = warehouses.find(w => w.id === loc.warehouse_id);
      const warehouseCode = warehouse?.code || 'SIN BODEGA';
      return {
        ...loc,
        label: `${loc.code} - ${warehouseCode} - ${loc.description}`,
        warehouse_code: warehouseCode,
      };
    });
  }, [locations, warehouses]);

  const resetForm = useCallback(() => {
    resetField('description');
    resetField('channel_type_id');
    resetField('price_range_id');
    resetField('container_category_id');
    resetField('container_status_id');
    resetField('container_recipient_type_id');
    resetField('location_id');
    setContainerType(undefined);
    setNewFiles([]);
    closeDialog(CREATE_CONTAINER_DIALOG);
  }, [closeDialog, resetField]);

  const onSubmit = useCallback(
    (values: any) => {
      if (companyId) {
        setLoading(true);
        // Asegurar que description sea siempre string para la API
        const payload = {
          ...values,
          description: values.description || ''
        };
        
        postContainer(
          companyId,
          payload,
          async (id: number, code: string) => {
            // Subir imágenes si hay archivos seleccionados
            if (newFiles.length > 0 && companyId) {
              let successCount = 0;
              let failureCount = 0;

              // Subir cada archivo secuencialmente
              for (const file of newFiles) {
                try {
                  await new Promise<void>((resolve, reject) => {
                    postContainerFile(
                      companyId,
                      id,
                      file,
                      () => {
                        successCount++;
                        resolve();
                      },
                      () => {
                        failureCount++;
                        console.error('Error al subir archivo:', file.name);
                        reject(new Error('Error al subir archivo'));
                      }
                    );
                  });
                } catch (error) {
                  // Error ya manejado en el callback, continuar con el siguiente archivo
                  console.error('Error al subir archivo:', error);
                }
              }

              // Mostrar mensaje si hubo errores (pero no bloquear el éxito de la creación)
              if (failureCount > 0) {
                console.warn(`${failureCount} imagen(es) no se pudieron subir`);
              }
            }

            // Función recursiva para encontrar una categoría (incluyendo subcategorías)
            const findCategoryRecursively = (categories: ContainerCategory[], categoryId: number): ContainerCategory | undefined => {
              for (const category of categories) {
                if (category.id === categoryId) {
                  return category;
                }
                if (category.child_categories && category.child_categories.length > 0) {
                  const found = findCategoryRecursively(category.child_categories as ContainerCategory[], categoryId);
                  if (found) {
                    return found;
                  }
                }
              }
              return undefined;
            };

            // Crear el objeto del contenedor creado para mostrar en el modal
            const selectedContainerType = containerTypes.find(t => t.id === containerType);
            const selectedCategory = findCategoryRecursively(containerCategories as ContainerCategory[], values.container_category_id);
            
            const containerData: ContainerSuccessData = {
              id,
              code,
              description: values.description || '',
              container_type_name: selectedContainerType?.name || selectedContainerType?.description || '',
              container_category_name: selectedCategory?.name || '',
              container_status_name: DESTINY_CONTAINER_TYPES.find(s => s.value === values.container_status_id)?.name || '',
              location_description: (() => {
                const selected = locationOptions.find(l => l.id === values.location_id);
                if (!selected) {
                  return 'Sin ubicación';
                }
                return selected.label;
              })(),
            };
            
            setCreatedContainer(containerData);
            setShowSuccessModal(true);
            resetForm();
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      }
    },
    [companyId, options, postContainer, postContainerFile, resetForm, getContainers, containerType, containerTypes, containerCategories, locationOptions, newFiles]
  );

  const filteredContainerCategories = useMemo(() => {
    return containerCategories.filter(
      (category) =>
        (category as ContainerCategory).container_type_id === containerType
    );
  }, [containerCategories, containerType]);

  return (
    <>
      <Dialog
        classes={{
          paper: 'm-8 sm:m-24 min-w-[350px] md:min-w-[700px]',
        }}
        onClose={resetForm}
        open={Array.isArray(dialogs) && dialogs.includes(CREATE_CONTAINER_DIALOG)}
      >
        <DialogContent>
          <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
            <div className="flex flex-col">
              <div className="mb-16 flex items-center">
                <Typography className="text-2xl font-bold truncate">
                  Crear Contenedor
                </Typography>
              </div>

              <div className="w-full max-w-3xl mt-20">
                <div className="flex flex-col space-y-32">
                  <form noValidate onSubmit={handleSubmit(onSubmit)}>

                    <div className="mb-24">
                      <Controller
                        name="container_status_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel id="container-status-label" required>
                              Estado
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="container-status-label"
                              label="Estado"
                              disabled={true}
                              required
                            >
                              {DESTINY_CONTAINER_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  {type.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </div>

                    <div className="mb-24">
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Descripción"
                            variant="outlined"
                            disabled={loading}
                            error={!!errors.description}
                            helperText={errors?.description?.message}
                            fullWidth
                          />
                        )}
                      />
                    </div>

                    <div className="mb-24">
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel id="container-type-label" required>
                          Tipo de Contenedor
                        </InputLabel>
                        <Select
                          labelId="container-type-label"
                          label="Tipo de Contenedor"
                          disabled={loading}
                          value={containerType}
                          onChange={(ev) => {
                            setContainerType(+ev.target.value);
                            // Limpiar categoría cuando cambia el tipo de contenedor
                            resetField('container_category_id');
                          }}
                          required
                        >
                          {containerTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>

                    <div className="mb-24">
                      <Controller
                        name="container_category_id"
                        control={control}
                        render={({ field }) => (
                          <CategorySelect
                            value={field.value || undefined}
                            onChange={(newValue) => {
                              if (typeof newValue === 'number') {
                                field.onChange(newValue);
                              } else {
                                field.onChange(undefined);
                              }
                            }}
                            categories={filteredContainerCategories.map(cat => ({
                              ...cat,
                              product_type_id: (cat as ContainerCategory).container_type_id || 0,
                              parent_id: (cat as any).parent_id || null,
                              child_categories: (cat as any).child_categories || [],
                            }))}
                            label="Categoría"
                            required
                            error={!!errors.container_category_id}
                            helperText={errors?.container_category_id?.message}
                            disabled={loading || !containerType}
                            size="medium"
                          />
                        )}
                      />
                    </div>

                    <div className="mb-24">
                      <Controller
                        name="container_recipient_type_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel id="recipient-type-label" required>
                              Tipo de Recipiente
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="recipient-type-label"
                              label="Tipo de Recipiente"
                              disabled={loading}
                              required
                              error={!!errors.container_recipient_type_id}
                            >
                              {containerRecipientTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                  {type.description || type.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.container_recipient_type_id && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {errors.container_recipient_type_id.message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </div>

                    <div className="mb-24">
                      <Controller
                        name="price_range_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel id="range-label">
                              Rango de Precios
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="range-label"
                              label="Rango de Precios"
                              disabled={loading}
                            >
                              {priceRanges.map((priceRange) => (
                                <MenuItem
                                  key={priceRange.id}
                                  value={priceRange.id}
                                >
                                  {priceRange.description}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </div>

                    <div className="mb-24">
                      <Controller
                        name="channel_type_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel id="channel-label">Canal</InputLabel>
                            <Select
                              {...field}
                              labelId="channel-label"
                              label="Canal"
                              disabled={loading}
                            >
                              {channelTypes.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  {category.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </div>

                    <div className="mb-24">
                      <Controller
                        name="location_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl variant="outlined" fullWidth>
                            <Autocomplete
                              options={locationOptions}
                              getOptionLabel={(option) => option.label}
                              value={
                                locationOptions.find(loc => loc.id === field.value) || null
                              }
                              onChange={(event, newValue) => {
                                field.onChange(newValue?.id || '');
                              }}
                              loading={locations.length === 0}
                              filterOptions={(options, { inputValue }) => {
                                const searchTerm = inputValue.toLowerCase();
                                return options.filter(option => 
                                  option.code.toLowerCase().includes(searchTerm) ||
                                  option.label.toLowerCase().includes(searchTerm) ||
                                  option.warehouse_code.toLowerCase().includes(searchTerm)
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Ubicación *"
                                  placeholder="Buscar por código o bodega..."
                                  required
                                  error={!!errors.location_id}
                                  helperText={errors?.location_id?.message}
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <Box sx={{ mr: 1 }}>
                                        <SearchOutlinedIcon sx={{ color: 'text.secondary' }} />
                                      </Box>
                                    ),
                                    endAdornment: (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {params.InputProps.endAdornment}
                                      </Box>
                                    ),
                                  }}
                                  disabled={loading}
                                />
                              )}
                              renderOption={(props, option) => (
                                <li {...props}>
                                  <Box>
                                    <Typography variant="body2" fontWeight="normal">
                                      {option.label}
                                    </Typography>
                                  </Box>
                                </li>
                              )}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: 'grey.300',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'primary.main',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                  },
                                },
                              }}
                            />
                          </FormControl>
                        )}
                      />
                    </div>

                    {/* Sección de Imágenes (Opcional) */}
                    <div className="mb-24">
                      <Typography variant="subtitle1" className="mb-16 font-semibold">
                        Imágenes del Contenedor (Opcional)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" className="mb-16 block">
                        Máximo {MAX_CONTAINER_FILES} imágenes
                      </Typography>
                      
                      <div className="mb-16">
                        <label htmlFor="container-image-input">
                          <Button
                            color="primary"
                            variant="outlined"
                            aria-label="upload picture"
                            component="span"
                            disabled={loading || newFiles.length >= MAX_CONTAINER_FILES}
                            startIcon={<PhotoCamera />}
                          >
                            Seleccionar Imágenes
                          </Button>
                        </label>
                        <input
                          id="container-image-input"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          multiple
                          onChange={(e) => {
                            if (e.target.files) {
                              const files = Array.from(e.target.files);
                              const remainingSlots = MAX_CONTAINER_FILES - newFiles.length;
                              const filesToAdd = files.slice(0, remainingSlots);
                              setNewFiles([...newFiles, ...filesToAdd]);
                              // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
                              e.target.value = '';
                            }
                          }}
                        />
                      </div>

                      {newFiles.length > 0 && (
                        <div className="flex flex-wrap gap-12 mt-16">
                          {newFiles.map((file, index) => (
                            <div
                              key={index}
                              className="relative w-[100px] h-[100px] rounded shadow overflow-hidden"
                            >
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`Preview ${index + 1}`}
                                className="object-cover w-full h-full"
                              />
                              <IconButton
                                color="error"
                                size="small"
                                aria-label="delete picture"
                                className="absolute top-0 right-0"
                                onClick={() => {
                                  const updatedFiles = newFiles.filter((_, i) => i !== index);
                                  setNewFiles(updatedFiles);
                                }}
                                sx={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  },
                                }}
                              >
                                <HighlightOffIcon fontSize="small" />
                              </IconButton>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {errorCode === ERROR.ERR_409 && (
                      <Typography
                        color="error"
                        className="text-md text-center mb-8"
                      >
                        Ya existe un contenedor con ese código
                      </Typography>
                    )}
                    <div className="flex justify-center">
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{ borderRadius: 50 }}
                        disabled={!isEmpty(errors) || !isValid || loading}
                        type="submit"
                        size="large"
                      >
                        <Spinner show={loading} />
                        Guardar
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de éxito */}
      <Dialog
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            minHeight: 400,
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'success.main' }}>
                ✓
              </Avatar>
              <Typography variant="h5" color="success.main" fontWeight="bold">
                ¡Contenedor Creado Exitosamente!
              </Typography>
            </Box>
            <Button 
              onClick={() => setShowSuccessModal(false)}
              size="small"
              color="error"
              variant="text"
            >
              ✕
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent>
          {createdContainer && (
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Información principal del contenedor */}
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Grid container spacing={3}>
                  {/* Icono del contenedor */}
                  <Grid item xs={12} md={4}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                      <Avatar
                        sx={{
                          width: 120,
                          height: 120,
                          bgcolor: 'primary.main',
                          fontSize: '3rem'
                        }}
                      >
                        📦
                      </Avatar>
                    </Box>
                  </Grid>

                  {/* Detalles del contenedor */}
                  <Grid item xs={12} md={8}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {createdContainer.code}
                      </Typography>
                      
                      {createdContainer.description && (
                        <Typography variant="body1" color="text.secondary">
                          <strong>Descripción:</strong> {createdContainer.description}
                        </Typography>
                      )}
                      
                      <Typography variant="body2" color="text.secondary">
                        <strong>Tipo:</strong> {createdContainer.container_type_name}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        <strong>Categoría:</strong> {createdContainer.container_category_name}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        <strong>Estado:</strong> {createdContainer.container_status_name}
                      </Typography>
                      
                      {createdContainer.location_description && createdContainer.location_description !== 'Sin ubicación' && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Ubicación:</strong> {createdContainer.location_description}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Código del contenedor */}
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Código del Contenedor
                </Typography>
                
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" color="text.secondary">
                      Código: {createdContainer.code}
                    </Typography>
                    <CopyButton
                      textToCopy={createdContainer.code || ''}
                      tooltip="Copiar código"
                    />
                  </Box>
                  
                  {/* Botón de imprimir etiqueta */}
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<PrintIcon />}
                    onClick={() => {
                      // Crear un objeto Container básico para la impresión
                      const containerForPrint: Container = {
                        id: createdContainer.id,
                        code: createdContainer.code,
                        description: createdContainer.description,
                        container_status_id: DESTINY_CONTAINER_TYPE.OPENED,
                        container_type_id: containerType || 0,
                        container_category_id: 0,
                        channel_type_id: 0,
                        price_range_id: 0,
                        location_id: 0,
                      };
                      printDestinationContainer(containerForPrint);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Imprimir Etiqueta
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setShowSuccessModal(false)}
            size="large"
          >
            Volver
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateDialog;
